// src/components/mdx/client/visualizations/SubstitutionEffectChart.client.tsx

/**
 * Substitution Effect Chart
 * 
 * This component visualizes the impact of gradual versus abrupt creative substitution
 * strategies, showing how the performance metrics change when introducing new creative 
 * assets to replace fatiguing ones.
 * 
 * OPTIMIZATION NOTE:
 * This component uses centralized imports from '~/lib/charts' instead of direct imports
 * from 'recharts'. This approach provides several bundle optimization benefits:
 * 
 * 1. Shared module chunking - All chart components share the same recharts code chunks
 * 2. Enhanced tree-shaking - More efficient dead code elimination across the codebase
 * 3. Import cost amortization - The fixed overhead cost of importing recharts is shared
 * 4. Deduplication - Better handling of nested dependencies within recharts
 * 5. Consistent code splitting - Creates cleaner boundaries for code splitting
 * 
 * As our application contains multiple visualizations, this approach significantly
 * reduces the overall bundle size compared to direct imports from 'recharts'.
 */

"use client"

import { forwardRef, useCallback, useMemo, useState } from "react"

import { RefreshCw } from "lucide-react"

import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "~/lib/charts/client"
import { Label } from "~/components/Label"
import {
  applySubstitutionStrategy,
  calculateTotalRevenueDifference,
  generateSubstitutionEffectData,
  type SubstitutionEffectDataPoint,
  type SubstitutionStrategy
} from "~/components/mdx/client/visualizations/substitutionEffectData"
import { Button } from "~/components/ui/Button"
import { Switch } from "~/components/ui/Switch"
import { useMediaQuery } from "~/lib/hooks/useMediaQuery"
import { createBrowserSeed, createDeterministicRandom, hashSeed } from "~/lib/utils/random"
import { ChartVisibilityBoundary } from "~/components/mdx/client/visualizations/ChartVisibilityBoundary.client"

/**
 * Props for the ChartContainer component
 * @interface ChartContainerProps
 * @extends React.HTMLAttributes<HTMLDivElement>
 * @property {object} [config] - Configuration for chart series
 */
interface ChartContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  config?: Record<string, {
    label: string
    color: string
  }>
}

/**
 * A container component for charts with forwarded refs
 */
const ChartContainer = forwardRef<HTMLDivElement, ChartContainerProps>(
  ({ className, ...props }, ref) => {
    return (
      <div className={className} {...props} ref={ref}>
        {props.children}
      </div>
    )
  }
)
ChartContainer.displayName = "ChartContainer"

type CreativeDataPoint = SubstitutionEffectDataPoint

/**
 * Props for the custom tooltip component
 */
interface StrategyTooltipProps {
  active?: boolean
  isWeaned: boolean
  label?: number | string
  payload?: Array<{ payload?: CreativeDataPoint }>
}

/**
 * Custom tooltip component for the chart
 */
const CustomTooltip = ({ active, payload, label, isWeaned }: StrategyTooltipProps): JSX.Element | null => {
  if (active && payload?.length && payload[0]?.payload) {
    const data = payload[0].payload
    const isLearningPhase = data.learningPhase

    return (
      <div className="rounded-lg border border-gray-700 bg-gray-800/90 p-4 shadow-lg backdrop-blur-sm">
        <p className="mb-2 font-medium text-gray-300">Day: {label}</p>
        <div className="mb-2 border-b border-gray-700 pb-2">
          <p className="text-sm font-medium text-gray-300">Spend Allocation</p>
          <p className="text-sm text-gray-400">
            Fatigued Creative: <span className="font-medium text-red-400">${data.fatiguedCreativeSpend.toLocaleString()}</span>
          </p>
          <p className="text-sm text-gray-400">
            New Creative: <span className="font-medium text-green-400">${data.newCreativeSpend.toLocaleString()}</span>
          </p>
        </div>
        <div className="mb-2 border-b border-gray-700 pb-2">
          <p className="text-sm font-medium text-gray-300">Performance</p>
          {data.fatiguedCreativeSpend > 0 && data.fatiguedCreativeROAS !== null && (
            <p className="text-sm text-gray-400">
              Fatigued Creative ROAS: <span className="font-medium text-red-400">{data.fatiguedCreativeROAS.toFixed(2)}x</span>
            </p>
          )}
          {data.newCreativeSpend > 0 && data.newCreativeROAS !== null && (
            <p className="text-sm text-gray-400">
              New Creative ROAS: <span className="font-medium text-green-400">{data.newCreativeROAS.toFixed(2)}x</span>
              {isLearningPhase && <span className="ml-2 text-xs font-bold text-blue-400">(Learning Phase)</span>}
            </p>
          )}
          <p className="text-sm font-medium text-gray-400">
            Weighted ROAS: <span className="font-medium text-purple-400">{data.weightedROAS.toFixed(2)}x</span>
          </p>
          {isWeaned ? (
            <p className="text-sm text-gray-400">
              Abrupt Strategy ROAS: <span className="font-medium text-gray-400">{data.abruptWeightedROAS.toFixed(2)}x</span>
            </p>
          ) : (
            <p className="text-sm text-gray-400">
              Weaned Strategy ROAS: <span className="font-medium text-gray-400">{data.weanedWeightedROAS.toFixed(2)}x</span>
            </p>
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-300">Revenue Impact</p>
          <p className="text-sm text-gray-400">
            Current Strategy Revenue: <span className="font-medium text-purple-400">
              ${isWeaned ? data.weanedRevenue?.toLocaleString() : data.abruptRevenue?.toLocaleString()}
            </span>
          </p>
          <p className="text-sm text-gray-400">
            Revenue Difference: <span className={`font-medium ${data.revenueDifference && data.revenueDifference > 0 ? "text-green-400" : "text-red-400"}`}>
              {data.revenueDifference && data.revenueDifference > 0 ? "+" : ""}${data.revenueDifference?.toLocaleString()}
            </span>
          </p>
        </div>
      </div>
    )
  }
  return null
}

/**
 * Props for the SubstitutionEffectChartContent component
 */
interface SubstitutionEffectChartContentProps {
  /** Whether the chart is visible. Controls refresh animation work. */
  isActive: boolean
}

/**
 * Substitution Effect Chart Content Component
 * Visualizes the substitution effect between fatigued and new creatives
 * with dual-axis for spend and ROAS metrics
 */
function SubstitutionEffectChartContent({ isActive }: SubstitutionEffectChartContentProps): JSX.Element {
  const numPoints = 100
  const [isWeaned, setIsWeaned] = useState(true)
  const [showRefreshHint, setShowRefreshHint] = useState(false)
  const [seed, setSeed] = useState(() => hashSeed("substitution-effect", numPoints))

  const baseData = useMemo<CreativeDataPoint[]>(() => {
    const rng = createDeterministicRandom(seed)
    return generateSubstitutionEffectData(numPoints, rng)
  }, [seed, numPoints])

  const displayData = useMemo<CreativeDataPoint[]>(() => {
    const strategy: SubstitutionStrategy = isWeaned ? "weaned" : "abrupt"
    return applySubstitutionStrategy(baseData, strategy, numPoints)
  }, [baseData, isWeaned, numPoints])

  const totalRevenueDifference = useMemo(() => {
    return calculateTotalRevenueDifference(displayData)
  }, [displayData])

  const regenerateData = useCallback(() => {
    setSeed(createBrowserSeed())
    setShowRefreshHint(false)
  }, [])

  // Phase boundaries for reference areas
  const initialPhaseEnd = Math.floor(numPoints * 0.2)
  // Learning phase duration increased
  const learningPhaseEnd = initialPhaseEnd + Math.floor(numPoints * 0.2)

  const isMobile = useMediaQuery("(max-width: 768px)")
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)")
  const formatDollar = useCallback((value: number): string => `$${value.toLocaleString()}`, [])
  const shouldAnimateRefresh = isActive && !prefersReducedMotion

  return (
    <div className="overflow-hidden rounded-xl bg-gradient-to-br from-gray-900 to-gray-800 shadow-2xl sm:rounded-3xl">
      <div className="p-3 sm:p-8">
        <div className="mb-3 flex flex-col justify-between sm:mb-6 sm:flex-row sm:items-center">
          <div>
            <h2 className="mb-1 text-lg font-bold text-transparent sm:mb-2 sm:text-3xl bg-clip-text bg-gradient-to-r from-purple-400 to-green-400">
              The Substitution Effect
            </h2>
            <p className="text-xs text-gray-400 sm:text-base">Strategic creative management for sustained performance</p>
          </div>
          <div className="mt-2 flex items-center space-x-3 rounded-lg bg-gray-800/50 p-2 sm:mt-0">
            <div className="flex items-center space-x-2">
              <Label
                htmlFor="strategy-toggle"
                className={`text-xs sm:text-sm ${!isWeaned ? "text-blue-400 font-medium" : "text-gray-400"}`}
              >
                Abrupt
              </Label>
              <Switch
                id="strategy-toggle" 
                checked={isWeaned} 
                onCheckedChange={setIsWeaned}
                className="data-[state=checked]:bg-green-500"
              />
              <Label
                htmlFor="strategy-toggle"
                className={`text-xs sm:text-sm ${isWeaned ? "text-green-400 font-medium" : "text-gray-400"}`}
              >
                Weaned
              </Label>
            </div>
          </div>
        </div>

        <div className="mb-2 rounded-lg bg-gray-800/50 p-2 sm:mb-5 sm:p-3">
          <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
            <p className="text-xs text-gray-300 sm:text-sm">
              {isWeaned
                ? "Weaned Strategy: Gradually shifts budget from fatigued to new creative, maintaining higher overall performance during the transition."
                : "Abrupt Strategy: Quickly shifts all budget to new creative, causing a significant performance dip during the learning phase."}
            </p>
            <div
              className="relative group"
              onMouseEnter={() => setShowRefreshHint(true)}
              onMouseLeave={() => setShowRefreshHint(false)}
            >
              <Button
                variant="outline"
                size="sm"
                onClick={regenerateData}
                className="border-gray-600 bg-gray-700/50 text-gray-300 hover:bg-gray-600 hover:text-white"
              >
                <RefreshCw
                  className={`mr-1 h-3 w-3 sm:h-4 sm:w-4 ${shouldAnimateRefresh ? "animate-spin" : ""}`}
                />
                <span className="text-xs">New Scenario</span>
              </Button>
              {showRefreshHint && (
                <div className="absolute bottom-full left-1/2 z-10 mb-2 w-48 -translate-x-1/2 transform rounded bg-gray-900/95 p-2 text-xs text-gray-300 shadow-lg animate-fadeIn">
                  Try different random scenarios to see how the strategies perform in various conditions!
                  <div className="absolute bottom-0 left-1/2 h-2 w-2 -translate-x-1/2 translate-y-1/2 rotate-45 transform bg-gray-900/95" />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mb-3 rounded-lg border border-gray-700/50 bg-gray-800/70 p-2 shadow-inner sm:mb-4 sm:p-4">
          <div className="flex flex-col items-center justify-between gap-2 sm:flex-row">
            <div className="flex items-center">
              <span className="mr-2 text-xs text-gray-300 sm:text-sm">Revenue Impact:</span>
              <span className={`text-sm font-bold sm:text-lg ${totalRevenueDifference > 0 ? "text-green-400" : "text-red-400"}`}>
                {totalRevenueDifference > 0 ? "+" : ""}${totalRevenueDifference.toLocaleString()}
              </span>
            </div>
            <div className="text-center text-xs text-gray-400 sm:text-right">
              {isWeaned
                ? "Weaned strategy outperforms abrupt by this amount over the campaign period"
                : "Abrupt strategy underperforms weaned by this amount over the campaign period"}
            </div>
          </div>
        </div>

        <ChartContainer>
          <ResponsiveContainer width="100%" height={isMobile ? 350 : 500}>
            <ComposedChart
              data={displayData}
              margin={{
                top: 10,
                right: isMobile ? 5 : 10,
                left: isMobile ? 5 : 10,
                bottom: isMobile ? 5 : 0,
              }}
            >
              <CartesianGrid stroke="rgba(107, 114, 128, 0.3)" strokeDasharray="3 3" />
              <XAxis
                dataKey="day"
                stroke="rgba(156, 163, 175, 0.7)"
                tick={{ fill: "rgba(156, 163, 175, 0.7)", fontSize: isMobile ? 10 : 12 }}
                tickLine={{ stroke: "rgba(156, 163, 175, 0.7)" }}
                interval={isMobile ? 19 : 9} // Show every 20th tick on mobile, every 10th on desktop
              />
              <YAxis 
                yAxisId="left"
                stroke="rgba(156, 163, 175, 0.7)"
                tick={{ fill: "rgba(156, 163, 175, 0.7)", fontSize: isMobile ? 9 : 11 }}
                tickLine={{ stroke: "rgba(156, 163, 175, 0.7)" }}
                domain={[0, 1200]}
                tickFormatter={formatDollar}
                width={isMobile ? 35 : 50}
                label={!isMobile ? { 
                  value: "Spend ($)", 
                  angle: -90, 
                  position: "insideLeft",
                  style: { fill: "rgba(156, 163, 175, 0.7)", fontSize: 11 },
                  dy: 50
                } : undefined}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                stroke="rgba(156, 163, 175, 0.7)"
                tick={{ fill: "rgba(156, 163, 175, 0.7)", fontSize: isMobile ? 9 : 11 }}
                tickLine={{ stroke: "rgba(156, 163, 175, 0.7)" }}
                domain={[0, 6]}
                width={isMobile ? 30 : 40}
                label={!isMobile ? { 
                  value: "ROAS (x)", 
                  angle: 90, 
                  position: "insideRight",
                  style: { fill: "rgba(156, 163, 175, 0.7)", fontSize: 11 },
                  dy: -30
                } : undefined}
              />
              <Tooltip content={<CustomTooltip isWeaned={isWeaned} />} />
              
              {/* Spend metrics (left axis) */}
              <Area 
                yAxisId="left"
                type="monotone" 
                dataKey="fatiguedCreativeSpend" 
                name="Fatigued Creative Spend"
                stackId="1"
                stroke="rgba(248, 113, 113, 0.7)" 
                fill="rgba(248, 113, 113, 0.4)" 
              />
              <Area 
                yAxisId="left"
                type="monotone" 
                dataKey="newCreativeSpend" 
                name="New Creative Spend"
                stackId="1"
                stroke="rgba(74, 222, 128, 0.7)" 
                fill="rgba(74, 222, 128, 0.4)" 
              />
              
              {/* ROAS metrics (right axis) */}
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="fatiguedCreativeROAS" 
                name="Fatigued Creative ROAS"
                stroke="rgb(248, 113, 113)" 
                strokeWidth={isMobile ? 1.5 : 2}
                dot={false}
                connectNulls={true}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="newCreativeROAS" 
                name="New Creative ROAS"
                stroke="rgb(74, 222, 128)" 
                strokeWidth={isMobile ? 1.5 : 2}
                dot={false}
                connectNulls={true}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="weightedROAS" 
                name="Weighted ROAS"
                stroke="rgb(192, 132, 252)" 
                strokeWidth={isMobile ? 2 : 3}
                dot={false}
              />
              
              {/* Comparison line (only shown when relevant) */}
              {isWeaned && (
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="abruptWeightedROAS" 
                  name="Abrupt Strategy ROAS"
                  stroke="rgba(156, 163, 175, 0.7)" 
                  strokeWidth={isMobile ? 1.5 : 2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              )}
              {!isWeaned && (
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="weanedWeightedROAS" 
                  name="Weaned Strategy ROAS"
                  stroke="rgba(156, 163, 175, 0.7)" 
                  strokeWidth={isMobile ? 1.5 : 2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              )}
              
              {/* Learning phase reference area */}
              <ReferenceArea 
                x1={initialPhaseEnd} 
                x2={learningPhaseEnd} 
                y1={0} 
                y2={6} 
                yAxisId="right"
                fill="rgba(59, 130, 246, 0.15)" // blue-500 with opacity
                strokeOpacity={0}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartContainer>
        
        {/* Mobile-optimized legend */}
        <div className="mt-3 sm:mt-6">
          <div className="mb-3 flex flex-wrap justify-center gap-2 text-xs sm:mb-4 sm:gap-5">
            <div className="flex items-center">
              <div className="mr-1 h-2 w-2 rounded bg-red-400 sm:mr-2 sm:h-4 sm:w-4" />
              <span className="text-gray-400">Fatigued Creative</span>
            </div>
            <div className="flex items-center">
              <div className="mr-1 h-2 w-2 rounded bg-green-400 sm:mr-2 sm:h-4 sm:w-4" />
              <span className="text-gray-400">New Creative</span>
            </div>
            <div className="flex items-center">
              <div className="mr-1 h-2 w-2 rounded bg-purple-400 sm:mr-2 sm:h-4 sm:w-4" />
              <span className="text-gray-400">Current Strategy ROAS</span>
            </div>
            <div className="flex items-center">
              <div className="mr-1 h-2 w-2 rounded bg-gray-400 sm:mr-2 sm:h-4 sm:w-4" />
              <span className="text-gray-400">Alternative Strategy ROAS</span>
            </div>
            <div className="flex items-center">
              <div className="mr-1 h-2 w-2 rounded bg-blue-500 opacity-20 sm:mr-2 sm:h-4 sm:w-4" />
              <span className="text-gray-400">Learning Phase</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4">
            <div className="rounded-lg bg-gray-800/50 p-2 sm:p-3">
              <h3 className="mb-1 text-xs font-medium text-gray-300 sm:mb-2 sm:text-base">Key Insight</h3>
              <p className="text-xs text-gray-400">
                {isWeaned
                  ? "The weaned strategy maintains higher overall ROAS during the transition period by gradually shifting budget as the new creative learns and improves."
                  : "The abrupt strategy shows a significant ROAS drop during the learning phase, as all budget is immediately allocated to the unoptimized new creative."}
              </p>
            </div>

            <div className="rounded-lg bg-gray-800/50 p-2 sm:p-3">
              <h3 className="mb-1 text-xs font-medium text-gray-300 sm:mb-2 sm:text-base">Phase Breakdown</h3>
              <div className="grid grid-cols-2 gap-1 text-xs sm:gap-2">
                <div className="rounded bg-gray-700/30 p-1 sm:p-2">
                  <span className="text-gray-400">Initial (Days 0-{initialPhaseEnd})</span>
                </div>
                <div className="rounded bg-gray-700/30 p-1 sm:p-2">
                  <span className="text-gray-400">Learning (Days {initialPhaseEnd + 1}-{learningPhaseEnd})</span>
                </div>
                <div className="rounded bg-gray-700/30 p-1 sm:p-2">
                  <span className="text-gray-400">Growth (Days {learningPhaseEnd + 1}-70)</span>
                </div>
                <div className="rounded bg-gray-700/30 p-1 sm:p-2">
                  <span className="text-gray-400">Stabilization (Day 71+)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Substitution Effect Chart Component
 * Wraps the chart content with visibility boundary for performance optimization
 */
export default function SubstitutionEffectChart(): JSX.Element {
  return (
    <ChartVisibilityBoundary aria-label="Substitution effect spend vs ROAS visualization">
      {({ hasEntered, isIntersecting }) =>
        hasEntered ? <SubstitutionEffectChartContent isActive={isIntersecting} /> : null
      }
    </ChartVisibilityBoundary>
  )
}
